import type { EmployeeSearchItem } from '../../.generated/api-client';
import { positionLabel } from '../utils/position';

export interface ImpersonationHistoryEntry {
  employeeId: number;
  fullName: string;
  positionCn: string;
  storeLabel: string;
}

const NAMESPACE = 'imp:v1';
const HISTORY_KEY = `${NAMESPACE}:history`;
const HISTORY_MAX = 8;

function safeRead(key: string): string | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeWrite(key: string, value: string): void {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(key, value);
  } catch {
    /* swallow: private mode / quota / disabled storage */
  }
}

function safeRemove(key: string): void {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.removeItem(key);
  } catch {
    /* swallow */
  }
}

function isHistoryEntry(value: unknown): value is ImpersonationHistoryEntry {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  const record = value as Partial<ImpersonationHistoryEntry>;
  return (
    typeof record.employeeId === 'number' &&
    typeof record.fullName === 'string' &&
    typeof record.positionCn === 'string' &&
    typeof record.storeLabel === 'string'
  );
}

export function loadImpersonationHistory(): ImpersonationHistoryEntry[] {
  const raw = safeRead(HISTORY_KEY);
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      safeRemove(HISTORY_KEY);
      return [];
    }
    const entries = parsed.filter(isHistoryEntry);
    if (entries.length !== parsed.length) {
      safeWrite(HISTORY_KEY, JSON.stringify(entries.slice(0, HISTORY_MAX)));
    }
    return entries.slice(0, HISTORY_MAX);
  } catch {
    safeRemove(HISTORY_KEY);
    return [];
  }
}

export function saveImpersonationHistory(entry: ImpersonationHistoryEntry): ImpersonationHistoryEntry[] {
  const rest = loadImpersonationHistory().filter((item) => item.employeeId !== entry.employeeId);
  const next = [entry, ...rest].slice(0, HISTORY_MAX);
  safeWrite(HISTORY_KEY, JSON.stringify(next));
  return next;
}

export function clearImpersonationHistory(): void {
  safeRemove(HISTORY_KEY);
}

export function historyEntryFromEmployee(
  item: EmployeeSearchItem,
  actor?: Record<string, unknown>
): ImpersonationHistoryEntry {
  const actorName = actor?.full_name;
  const actorPosition = actor?.position_cn;
  return {
    employeeId: item.employee_id,
    fullName: typeof actorName === 'string' && actorName ? actorName : item.full_name,
    positionCn: positionLabel(actor?.role ?? item.position, actorPosition ?? item.position_cn),
    storeLabel: item.store_label ?? '',
  };
}

export const __TEST_ONLY__ = {
  HISTORY_KEY,
  HISTORY_MAX,
} as const;
